import React, { useState } from "react"
import { Box, Typography, Grid } from "@mui/material"
import CommonModal from "components/CommonModal"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppSelect from "components/StyledComponents/AppSelect"
import ToggleSwitch from "components/StyledComponents/ToggleSwitch"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import AppDatePicker from "components/AppDatePicker"
import StyledLabel from "components/StyledComponents/StyledLabel"
import InfoTooltip from "components/InfoTooltip"
import { useDispatch } from "react-redux"
import AppAutocomplete from "components/AppAutocomplete"
import ImageCropper from "components/ImageCropper"
import { superAdminAction } from "redux/superAdmin/slice/user"
import { uploadRewardImageAction } from "redux/superAdmin/actions"

const RewardForm = ({
  open,
  onClose,
  formik,
  isEdit = false,
  tiers = [],
  categories = [],
  cardTypes = []
}) => {
  const dispatch = useDispatch()
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)

  const handleImageDelete = (fieldName) => {
    formik.setFieldValue(fieldName, null)
    dispatch(superAdminAction.setUploadedImageId(""))
  }

  const onImageUpload = (image) => {
    if (image) {
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  function handleCroppedImage(image) {
    let data = new FormData()
    data.append("media", image)
    data.append("media_type", "IMAGE")
    data.append("content_type", image.type)
    image.preview = URL.createObjectURL(image)
    formik.setFieldValue("image", image)
    dispatch(uploadRewardImageAction({ data }))
  }

  const selectedType = cardTypes.find((ct) => ct.id === formik.values.card_type)
  const isDigital = selectedType?.delivery_type === "digital"

  const tierMenuItems = tiers.map((t) => ({ label: t.name, value: t.id }))
  const categoryMenuItems = categories
    .filter((c) => c.is_active !== false)
    .map((c) => ({ label: c.name, value: c.id }))
  const cardTypeItems = cardTypes.map((ct) => ({
    label: ct.title || ct.name || ct.delivery_type_label || ct.delivery_type,
    value: ct.id
  }))

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      actionButtons={
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pb: 2 }}>
          <AppButton onClick={formik.handleSubmit}>{isEdit ? "Update" : "Save"}</AppButton>
        </Box>
      }
      maxWidth="md">
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={1}
        onClose={setCrop}
        openCrop={openCrop}
      />
      <Box flexDirection="column">
        <Typography variant="h2" textAlign="center">
          {isEdit ? "Edit Reward" : "Add New Reward"}
        </Typography>
        <Grid container spacing={3} mt={2}>
          {/* Row 1 */}
          <Grid item xs={12} sm={4}>
            <AppSelect
              label="Reward Tier"
              isRequired
              isToolTip
              renderToolTip="Minimum membership tier a member must reach to redeem this reward. Members below this tier see it locked."
              isUpperCase={true}
              id="tier"
              name="tier"
              value={formik.values.tier}
              menuItems={tierMenuItems}
              onMenuClick={(item) => formik.setFieldValue("tier", item.value)}
              onChange={formik.handleChange}
              error={formik.touched.tier && Boolean(formik.errors.tier)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <AppSelect
              label="Delivery Type"
              isRequired
              isToolTip
              renderToolTip="How the reward is fulfilled. Digital delivers a coupon code automatically on redemption (you add codes below). Manual is fulfilled by your staff."
              isUpperCase={true}
              id="card_type"
              name="card_type"
              value={formik.values.card_type}
              menuItems={cardTypeItems}
              onMenuClick={(item) => formik.setFieldValue("card_type", item.value)}
              onChange={formik.handleChange}
              disabled={isDigital}
              error={formik.touched.card_type && Boolean(formik.errors.card_type)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <AppSelect
              label="Category"
              id="category"
              name="category"
              isToolTip
              renderToolTip="Optional grouping used to organise rewards for members in the app. Only active categories are shown."
              isUpperCase={true}
              value={formik.values.category}
              menuItems={categoryMenuItems}
              onMenuClick={(item) => formik.setFieldValue("category", item.value)}
              onChange={formik.handleChange}
              placeholder="Select Category"
            />
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={6}>
            <ImageUploadField
              id="image"
              name="image"
              label="Thumbnail"
              isRequired
              value={formik.values.image}
              onChange={(e) => onImageUpload(e, "image")}
              onDelete={() => handleImageDelete("image")}
              placeholder={"Upload Thumbnail"}
              error={formik.errors.image}
              touched={formik.touched.image}
              isVariant={true}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AppTextField
              fullWidth
              id="name"
              name="name"
              label="Reward Title"
              isRequired
              isUpperCase={true}
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              placeholder="Enter Prize Title"
            />
          </Grid>

          {/* Row 3 */}
          <Grid item xs={12}>
            <AppTextField
              fullWidth
              id="description"
              name="description"
              label="Reward Description"
              isRequired
              isUpperCase={true}
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              placeholder="Enter Prize Description"
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              fullWidth
              id="details"
              name="details"
              label="Details"
              isRequired
              isUpperCase={true}
              multiline
              rows={3}
              value={formik.values.details}
              onChange={formik.handleChange}
              error={formik.touched.details && Boolean(formik.errors.details)}
              helperText={formik.touched.details && formik.errors.details}
              placeholder="Enter Details"
            />
          </Grid>
          {/* Row 4 */}
          <Grid item xs={12} sm={6}>
            <AppTextField
              fullWidth
              id="coin_cost"
              name="coin_cost"
              label="Cost"
              isRequired
              isToolTip
              renderToolTip="Coins a member spends to redeem this reward. Members without enough available coins see it locked."
              isUpperCase={true}
              type="number"
              value={formik.values.coin_cost}
              onChange={formik.handleChange}
              error={formik.touched.coin_cost && Boolean(formik.errors.coin_cost)}
              helperText={formik.touched.coin_cost && formik.errors.coin_cost}
              placeholder="Enter Cost"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AppTextField
              fullWidth
              id="total_redemption_limit"
              name="total_redemption_limit"
              label="Quantity Available"
              isRequired
              isToolTip
              renderToolTip="Total units offered. During an active Shopping Spree this is the quantity for the current spree and resets each spree. The reward is sold out once this many are redeemed."
              isUpperCase={true}
              type="number"
              value={formik.values.total_redemption_limit || ""}
              onChange={(e) => {
                const limit = e.target.value
                formik.setFieldValue("inventory_limited", true)
                formik.setFieldValue("total_redemption_limit", limit)

                if (isDigital && !isEdit) {
                  const num = Number(limit) || 0
                  const currentCodes = formik.values.coupon_codes || []
                  if (currentCodes.length > num) {
                    formik.setFieldValue("coupon_codes", currentCodes.slice(0, num))
                  }
                }
              }}
              error={
                formik.touched.total_redemption_limit &&
                Boolean(formik.errors.total_redemption_limit)
              }
              helperText={
                formik.touched.total_redemption_limit && formik.errors.total_redemption_limit
              }
              placeholder="Enter Quantity"
            />
          </Grid>

          {isDigital && (
            <Grid item xs={12}>
              <AppAutocomplete
                id={"coupon_codes"}
                isUpperCase={true}
                placeholder={`Enter Coupon Codes`}
                label={"Coupon Codes"}
                value={formik.values.coupon_codes}
                error={formik.touched.coupon_codes && Boolean(formik.errors.coupon_codes)}
                helperText={formik.touched.coupon_codes && formik.errors.coupon_codes}
                touched={formik.touched.coupon_codes}
                onChange={(event, newValue) => formik.setFieldValue("coupon_codes", newValue)}
              />
            </Grid>
          )}

          {/* Row 5 */}
          <Grid item sm={12} lg={6} className="datesContainer">
            <StyledLabel variant="body1Regular">
              {"Valid From"}
              <span>*</span>
              <InfoTooltip
                title="The reward is only shown and redeemable on or after this date."
                sx={{ ml: 0.75 }}
              />
            </StyledLabel>
            <Box className="dateFieldContainer w-100 mt-2">
              <AppDatePicker
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 6))}
                inputFormat="dd MMM yyyy"
                disableMaskedInput
                key={"valid_from"}
                value={formik.values.valid_from}
                onChange={(val) => formik.setFieldValue("valid_from", val)}
                error={formik.errors.valid_from}
                touched={formik.touched.valid_from}
              />
            </Box>
          </Grid>
          <Grid item sm={12} lg={6} className="datesContainer">
            <StyledLabel variant="body1Regular">
              {"Valid Until"}
              <span>*</span>
              <InfoTooltip
                title="The reward stops being shown and redeemable after this date."
                sx={{ ml: 0.75 }}
              />
            </StyledLabel>
            <Box className="dateFieldContainer w-100 mt-2">
              <AppDatePicker
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 6))}
                inputFormat="dd MMM yyyy"
                disableMaskedInput
                key={"valid_until"}
                value={formik.values.valid_until}
                onChange={(val) => formik.setFieldValue("valid_until", val)}
                error={formik.errors.valid_until}
                touched={formik.touched.valid_until}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <AppTextField
              fullWidth
              id="per_user_redemption_limit"
              name="per_user_redemption_limit"
              label="Per User Limit"
              isRequired
              isToolTip
              renderToolTip="Maximum times a single member can redeem this reward. During a Shopping Spree this resets each spree window."
              isUpperCase={true}
              type="number"
              value={formik.values.per_user_redemption_limit}
              onChange={formik.handleChange}
              placeholder="e.g. 1"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mt: 3, display: "flex", alignItems: "center", ml: 1 }}>
              <ToggleSwitch
                checked={formik.values.poweredBy}
                onChange={(e) => formik.setFieldValue("poweredBy", e.target.checked)}
              />
              <Typography variant="body2Regular" sx={{ ml: 1.5, color: "#fff" }}>
                Powered by
              </Typography>
              <InfoTooltip
                title="When on, a 'Powered by' attribution tag is shown on this reward in the app."
                sx={{ ml: 0.75 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mt: 3, display: "flex", alignItems: "center", ml: 1 }}>
              <ToggleSwitch
                checked={formik.values.is_enabled}
                onChange={(e) => formik.setFieldValue("is_enabled", e.target.checked)}
              />
              <Typography variant="body2Regular" sx={{ ml: 1.5, color: "#fff" }}>
                Reward is Enabled
              </Typography>
              <InfoTooltip
                title="Master switch. When off, this reward is hidden from members regardless of tier, dates or quantity."
                sx={{ ml: 0.75 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </CommonModal>
  )
}

export default RewardForm
